import React, { useState, useEffect } from 'react';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { styled } from '@uhg-abyss/web/tools/styled';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { event } from '@uhg-abyss/web/tools/event';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { useWindowSize } from '../../../../tools/useWindowSize';
import { CustomTabLabel } from './CustomTabLabel';

const StyledBox = styled('div', {
  position: 'relative',
  minHeight: '100%',
  padding: '0 !important',
  width: '100%',
  backgroundColor: 'transparent',
});

const InlineFlexDiv = styled('div', {
  display: 'inline-flex',
  alignItems: 'end',
  marginBottom: '15px',
  gap: '10px',
});

const PaddedCard = styled(Card, {
  padding: '20px 25px',
});

const StyledCardContainer = styled('div', {
  display: 'flex',
  width: '100%',
  gap: '15px',
});

export const FiveTabWidget = () => {
  const { data } = usePropsContext();
  const { tabIndex, setTabIndex, widgetTabs, permissionsContext, breakPoint } =
    data;
  const [currentTab, setCurrentTab] = useState({});
  const { width } = useWindowSize();

  const handleTabChange = (index) => {
    setTabIndex(index);
    event('UHCPP_CUSTOMIZED_TAB_WIDGET_CLICKS', {
      tabName: widgetTabs[index].name,
      tabContext: permissionsContext,
    });
  };

  useEffect(() => {
    setCurrentTab(widgetTabs[tabIndex]);
  }, [widgetTabs, tabIndex]);

  return (
    <React.Fragment>
      <div
        data-testid='right-rail-wrapper'
        style={{
          zIndex: -1,
          position: 'absolute',
          height: '800px',
          width: '29%',
          right: '0px',
          top: '0px',
        }}
      />
      <Choose>
        <When condition={widgetTabs.length > 1}>
          <StyledBox data-testid='five-tab-widget'>
            <Layout.Stack grow space={45}>
              <Tabs
                size='$lg'
                title='Five Tab Widget'
                variant='arrow'
                display='column'
                css={{
                  'abyss-tabs-list': {
                    width: '25%',
                    '.abyss-tabs-tab': {
                      minHeight: '72px !important',
                    },
                  },
                  'abyss-tabs-content-container': {
                    // if height is specified, overflow is auto-set to scroll
                    overflow: 'none',
                    paddingBottom: '48px !important',
                  },
                }}
                onTabChange={handleTabChange}
                active={tabIndex}
                grow={false}
              >
                <For each='tab' index='index' of={widgetTabs}>
                  <Tabs.Tab key={index} label={<CustomTabLabel {...tab} />}>
                    {tab?.component}
                  </Tabs.Tab>
                </For>
              </Tabs>
            </Layout.Stack>
          </StyledBox>
          <If condition={currentTab && width <= breakPoint}>
            <div style={{ position: 'relative' }}>
              <div
                data-testid='right-rail-collapsed-wrapper'
                style={{
                  zIndex: -1,
                  position: 'absolute',
                  height: '98%',
                  width:
                    currentTab.rightRail && currentTab.rightRail.length === 1
                      ? '50%'
                      : '100%',
                  left: '0px',
                  top: '2%',
                }}
              />
              <If
                condition={
                  currentTab.name !== 'uhcUpdates' &&
                  currentTab.name !== 'actionRequired'
                }
              >
                <Heading
                  offset={1}
                  css={{ 'abyss-heading-root': { padding: '10px 0px' } }}
                >
                  {`Additional ${currentTab.label} Items`}
                </Heading>
              </If>
              <If
                condition={
                  currentTab.rightRail && currentTab.rightRail.length > 0
                }
              >
                <Choose>
                  <When condition={currentTab.rightRail.length === 2}>
                    <StyledCardContainer>
                      <For each='rail' of={currentTab.rightRail} index='index'>
                        <Card
                          key={index}
                          css={{
                            'abyss-card-root': {
                              width: '50%',
                              marginTop: '0px !important',
                            },
                          }}
                        >
                          {rail}
                        </Card>
                      </For>
                    </StyledCardContainer>
                  </When>
                  <Otherwise>
                    <Card
                      css={{
                        'abyss-card-root': {
                          height: '600px',
                          width: '50%',
                        },
                      }}
                    >
                      {currentTab.rightRail[0]}
                    </Card>
                  </Otherwise>
                </Choose>
              </If>
            </div>
          </If>
        </When>
        <Otherwise>
          {/* When only UHC Updates is visible */}
          <PaddedCard>
            <InlineFlexDiv>
              <IconBrand
                icon={widgetTabs[0]?.iconName}
                size='30px'
                brand='uhc'
              />
              <Text fontWeight='bold' size={24} color='$primary1'>
                {widgetTabs[0]?.label}
              </Text>
              <Text size={14} color='$gray6'>
                {widgetTabs[0]?.subText}
              </Text>
            </InlineFlexDiv>
            {widgetTabs[0]?.component}
          </PaddedCard>
        </Otherwise>
      </Choose>
    </React.Fragment>
  );
};
